import { Link } from 'react-router'
import { ChartCard } from '@/components/patterns/ChartCard'
import { formatDatum } from '@/lib/format'
import { relationHref, rhythmHref } from './links'
import { plural } from './text'
import type { Rhythm, RhythmItem, Scope } from './types'

const sinceText = (days: number | null): string => {
  if (days === null) return ' · nog geen contact'
  if (days === 0) return ' · vandaag'
  return ` · ${plural(days, 'dag', 'dagen')} geleden`
}

function RelationName({ item }: { item: RhythmItem }) {
  if (!item.relation) return <>Onbekende relatie</>
  return (
    <Link to={relationHref(item.relation.type, item.relation.id, 'afspraken')} className="font-medium underline-offset-2 hover:underline">
      {item.relation.name}
    </Link>
  )
}

/** Widget 4: contact rhythm as one stacked bar (op schema · binnenkort · gemist) with the worst and the next (design §4.1). */
export function RhythmWidget({ rhythm, scope }: { rhythm: Rhythm; scope: Scope }) {
  const total = rhythm.on_track + rhythm.due_soon + rhythm.missed
  const segments = [
    { key: 'op_schema', count: rhythm.on_track, label: 'op schema', className: 'bg-success' },
    { key: 'binnenkort', count: rhythm.due_soon, label: 'binnenkort', className: 'bg-warning' },
    { key: 'gemist', count: rhythm.missed, label: 'gemist', className: 'bg-danger' },
  ] as const
  return (
    <ChartCard
      title="Contactritme"
      definition="Hoe het staat met de afgesproken contactmomenten"
      empty={total === 0 ? { title: 'Nog geen contactritmes.', description: 'Leg er een vast bij een relatie.' } : null}
    >
      <div className="flex h-3 w-full gap-0.5 overflow-hidden rounded-full" role="group" aria-label="Verdeling contactritme">
        {segments
          .filter((segment) => segment.count > 0)
          .map((segment) => (
            <Link
              key={segment.key}
              to={rhythmHref(segment.key, scope)}
              aria-label={`${segment.count} ${segment.label}`}
              style={{ flexGrow: segment.count }}
              className={segment.className}
            />
          ))}
      </div>
      <p className="text-sm tabular-nums text-foreground">
        {rhythm.on_track} op schema · {rhythm.due_soon} binnenkort · {rhythm.missed} gemist
      </p>
      {rhythm.worst.length > 0 ? (
        <div className="flex flex-col gap-0.5 text-sm">
          <p className="font-semibold">Gemist</p>
          {rhythm.worst.map((item) => (
            <p key={item.agreement_id} className="text-muted-foreground">
              <RelationName item={item} /> · ritme {item.rhythm}
              {sinceText(item.days_since_contact)}
            </p>
          ))}
        </div>
      ) : null}
      {rhythm.next.length > 0 ? (
        <div className="flex flex-col gap-0.5 text-sm">
          <p className="font-semibold">Binnenkort</p>
          {rhythm.next.map((item) => (
            <p key={item.agreement_id} className="text-muted-foreground">
              <RelationName item={item} />
              {item.next_due_on ? ` (${formatDatum(item.next_due_on)})` : ''}
            </p>
          ))}
        </div>
      ) : null}
    </ChartCard>
  )
}
